import { MappedUserField, OrgUnitMatchBy, Role, TransformType } from '@today/types'
import { z } from 'zod'

// ─── Identity attribute mapping (TO-377, spec identity-attribute-mapping) ────
// Shape only → 422 VALIDATION_ERROR. The semantic refusals (admin role targets,
// transform parameters, unmapped EMAIL) carry their own codes and run in
// `@today/core-utils/identity-mapping` `checkMappingSemantics`.

const idpName = z.string().trim().min(1).max(256)

const transformSchema = z
	.object({
		type: z.enum(TransformType),
		separator: z.string().optional(),
		pattern: z.string().optional(),
		table: z.record(z.string(), z.string()).optional(),
	})
	.strict()

const fieldRuleSchema = z.object({ idpAttribute: idpName, transform: transformSchema }).strict()

const fieldsShape = Object.fromEntries(
	Object.values(MappedUserField).map((field) => [field, fieldRuleSchema.nullable()]),
) as Record<MappedUserField, z.ZodNullable<typeof fieldRuleSchema>>

/** A provider's mapping (REQ-001): all eight field keys, org-unit rule, role rules. */
export const attributeMappingSchema = z
	.object({
		fields: z.object(fieldsShape).strict(),
		orgUnit: z
			.object({ idpAttribute: idpName, transform: transformSchema, matchBy: z.enum(OrgUnitMatchBy) })
			.strict()
			.nullable(),
		roles: z
			.object({
				idpAttribute: idpName,
				transform: transformSchema,
				rules: z.array(z.object({ idpValue: z.string().min(1).max(256), role: z.enum(Role) }).strict()).max(50),
				defaultRole: z.enum(Role),
			})
			.strict()
			.nullable(),
	})
	.strict()

/** `PUT …/attribute-mapping` (REQ-002). */
export const attributeMappingSaveSchema = z
	.object({ baseVersion: z.number().int().min(0), mapping: attributeMappingSchema })
	.strict()

const sampleValue = z.union([z.string().max(2048), z.array(z.string().max(2048)).min(1).max(100)])

/** A sample: 0–200 attributes keyed by IdP attribute name (REQ-008). */
export const sampleAttributesSchema = z
	.record(z.string().min(1).max(256), sampleValue)
	.refine((attributes) => Object.keys(attributes).length <= 200, { message: 'At most 200 attributes' })

/** `POST …/attribute-mapping/preview` (REQ-008). */
export const attributeMappingPreviewSchema = z
	.object({ mapping: attributeMappingSchema, sample: z.object({ attributes: sampleAttributesSchema }).strict() })
	.strict()

/** `GET …/attribute-mapping/versions` query (REQ-006). */
export const attributeMappingVersionsQuerySchema = z.object({
	page: z.coerce.number().int().min(1).default(1),
	limit: z.coerce.number().int().min(1).max(100).default(20),
})
